import React, { useState, useEffect } from 'react';
import { trainingProgramService } from '../services/trainingProgram';
import { useAuth } from '../contexts/AuthContext';
import type { TrainingProgram, ProgramWorkout } from '../types';

interface WorkoutSelectorProps {
  onWorkoutSelect: (workout: ProgramWorkout) => void;
  onCancel: () => void;
}

/**
 * WorkoutSelector - Component for selecting a workout to perform
 * Shows active program workouts and allows quick workout selection
 * Requirements: 5.3, 6.1
 */
export const WorkoutSelector: React.FC<WorkoutSelectorProps> = ({
  onWorkoutSelect,
  onCancel
}) => {
  const { user } = useAuth();
  const [activeProgram, setActiveProgram] = useState<TrainingProgram | null>(null);
  const [allPrograms, setAllPrograms] = useState<TrainingProgram[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('Development mode: using mock programs for workout selection');
        
        // Create mock programs with workouts
        const mockPrograms: TrainingProgram[] = [
          {
            id: 'mock-program-1',
            user_id: 'mock-user-123456789',
            name: 'Программа для начинающих',
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            workouts: [
              {
                id: 'mock-workout-1',
                program_id: 'mock-program-1',
                name: 'Тренировка A - Верх тела',
                order_index: 0,
                exercises: [
                  {
                    id: 'mock-pe-1',
                    workout_id: 'mock-workout-1',
                    order_index: 0,
                    target_sets: 3,
                    target_reps_min: 8,
                    target_reps_max: 12,
                    exercise: {
                      id: 'mock-1',
                      name: 'Bench Press',
                      name_ru: 'Жим лёжа',
                      muscle_group: 'Грудь',
                      equipment: 'Штанга',
                      instructions: 'Лягте на скамью, возьмите штангу широким хватом, опустите к груди и выжмите вверх.'
                    }
                  },
                  {
                    id: 'mock-pe-2',
                    workout_id: 'mock-workout-1',
                    order_index: 1,
                    target_sets: 3,
                    target_reps_min: 6,
                    target_reps_max: 10,
                    exercise: {
                      id: 'mock-4',
                      name: 'Pull-ups',
                      name_ru: 'Подтягивания',
                      muscle_group: 'Спина',
                      equipment: 'Турник',
                      instructions: 'Повисните на турнике, подтянитесь до касания подбородком перекладины.'
                    }
                  }
                ]
              },
              {
                id: 'mock-workout-2',
                program_id: 'mock-program-1',
                name: 'Тренировка B - Низ тела',
                order_index: 1,
                exercises: [
                  {
                    id: 'mock-pe-3',
                    workout_id: 'mock-workout-2',
                    order_index: 0,
                    target_sets: 4,
                    target_reps_min: 8,
                    target_reps_max: 12,
                    exercise: {
                      id: 'mock-2',
                      name: 'Squat',
                      name_ru: 'Приседания',
                      muscle_group: 'Ноги',
                      equipment: 'Штанга',
                      instructions: 'Поставьте штангу на плечи, приседайте до параллели с полом, поднимайтесь вверх.'
                    }
                  },
                  {
                    id: 'mock-pe-4',
                    workout_id: 'mock-workout-2',
                    order_index: 1,
                    target_sets: 3,
                    target_reps_min: 10,
                    target_reps_max: 15,
                    exercise: {
                      id: 'mock-9',
                      name: 'Lunges',
                      name_ru: 'Выпады',
                      muscle_group: 'Ноги',
                      equipment: 'Собственный вес',
                      instructions: 'Сделайте шаг вперёд, опуститесь в выпад, вернитесь в исходное положение.'
                    }
                  }
                ]
              }
            ]
          },
          {
            id: 'mock-program-2',
            user_id: 'mock-user-123456789',
            name: 'Силовая программа',
            is_active: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            workouts: [
              {
                id: 'mock-workout-3',
                program_id: 'mock-program-2',
                name: 'Тренировка C - Сила',
                order_index: 0,
                exercises: [
                  {
                    id: 'mock-pe-5',
                    workout_id: 'mock-workout-3',
                    order_index: 0,
                    target_sets: 5,
                    target_reps_min: 3,
                    target_reps_max: 5,
                    exercise: {
                      id: 'mock-3',
                      name: 'Deadlift',
                      name_ru: 'Становая тяга',
                      muscle_group: 'Спина',
                      equipment: 'Штанга',
                      instructions: 'Наклонитесь к штанге, возьмите её и поднимите, выпрямляя спину и ноги.'
                    }
                  }
                ]
              }
            ]
          }
        ];

        setAllPrograms(mockPrograms);
        const active = mockPrograms.find(p => p.is_active);
        setActiveProgram(active || null);
        if (active) {
          setSelectedProgramId(active.id);
        }
        return;
      }

      // Real implementation
      console.log('Loading programs for user:', user?.id);
      
      if (!user?.id) {
        setError('Пользователь не авторизован');
        setIsLoading(false);
        return;
      }
      
      const programs = await trainingProgramService.getAllPrograms(user.id);
      console.log('Programs loaded:', programs.length);
      
      setAllPrograms(programs);
      
      const active = programs.find((p: TrainingProgram) => p.is_active);
      setActiveProgram(active || null);
      if (active) {
        setSelectedProgramId(active.id);
      }
    } catch (err) {
      console.error('Failed to load programs:', err);
      setError('Не удалось загрузить программы тренировок');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedProgram = allPrograms.find(p => p.id === selectedProgramId);
  const workouts = selectedProgram?.workouts || [];

  if (isLoading) {
    return (
      <div className="workout-selector p-4 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка программ...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workout-selector p-4 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadPrograms}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (allPrograms.length === 0) {
    return (
      <div className="workout-selector p-4 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📋</div>
          <h2 className="text-xl font-semibold mb-2">Нет программ тренировок</h2>
          <p className="text-gray-600 mb-4">
            Создайте программу тренировок, чтобы начать заниматься
          </p>
          <button
            onClick={onCancel}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Создать программу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="workout-selector p-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">Выбор тренировки</h1>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 p-2"
          title="Отмена"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Active Program Highlight */}
      {activeProgram && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center mb-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
              Активная
            </span>
            <h2 className="font-semibold">{activeProgram.name}</h2>
          </div>
          <p className="text-sm text-blue-700">
            Рекомендуем выбрать тренировку из активной программы
          </p>
        </div>
      )}

      {/* Program Selector */}
      {allPrograms.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Программа тренировок
          </label>
          <select
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Выберите программу</option>
            {allPrograms.map(program => (
              <option key={program.id} value={program.id}>
                {program.name} {program.is_active ? '(активная)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Workouts List */}
      {selectedProgram && (
        <div>
          <h3 className="font-medium mb-4">
            Тренировки в программе "{selectedProgram.name}"
          </h3>
          
          {workouts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🏃‍♂️</div>
              <p>В этой программе нет тренировок</p>
            </div>
          ) : (
            <div className="space-y-3">
              {workouts.map(workout => {
                const totalExercises = workout.exercises.length;
                const estimatedTime = totalExercises * 8; // Примерно 8 минут на упражнение
                
                return (
                  <div
                    key={workout.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => onWorkoutSelect(workout)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-lg">{workout.name}</h4>
                      <div className="text-sm text-gray-500">
                        ~{estimatedTime}мин
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      {totalExercises} упражнений
                    </div>
                    
                    {/* Exercise Preview */}
                    <div className="space-y-1">
                      {workout.exercises.slice(0, 3).map((exercise, index) => (
                        <div key={exercise.id} className="text-sm text-gray-700">
                          <span className="font-medium">{index + 1}.</span> {exercise.exercise.name_ru}
                          {exercise.target_sets && (
                            <span className="text-gray-500 ml-2">
                              ({exercise.target_sets} × {exercise.target_reps_min}-{exercise.target_reps_max})
                            </span>
                          )}
                        </div>
                      ))}
                      {workout.exercises.length > 3 && (
                        <div className="text-sm text-gray-500">
                          ... и ещё {workout.exercises.length - 3} упражнений
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Группы мышц: {[...new Set(workout.exercises.map(e => e.exercise.muscle_group))].join(', ')}
                        </div>
                        <div className="text-blue-600 font-medium text-sm">
                          Начать тренировку →
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Quick Start Tips */}
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">💡 Советы для тренировки:</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Подготовьте воду и полотенце</li>
          <li>• Сделайте разминку перед началом</li>
          <li>• Следите за техникой выполнения</li>
          <li>• Отдыхайте между подходами 1-3 минуты</li>
        </ul>
      </div>
    </div>
  );
};

export default WorkoutSelector;